import React, { useEffect, useRef } from 'react';

// Hiệu ứng "nổ quà": bắn confetti từ tâm ra (canvas thuần, KHÔNG thư viện). Bắn 1 lần rồi tự tắt.
// colors: dải màu theo hạng hộp để hợp tông. Gọi lại onDone khi hạt tàn (tuỳ chọn).
type Props = { colors?: string[]; fire?: boolean; onDone?: () => void };

const DEFAULT_COLORS = ['#f59e0b', '#f43f5e', '#a855f7', '#38bdf8', '#22c55e', '#ffffff'];

const RewardBurst: React.FC<Props> = ({ colors = DEFAULT_COLORS, fire = true, onDone }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!fire) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = (canvas.width = canvas.offsetWidth);
    const H = (canvas.height = canvas.offsetHeight);
    const cx = W / 2;
    const cy = H * 0.42;

    // Tạo hạt: bắn tỏa tròn + hơi lệch lên, kích thước/tốc độ ngẫu nhiên.
    const N = Math.min(160, Math.round((W * H) / 900));
    const parts = Array.from({ length: N }, (_, i) => {
      const ang = (Math.PI * 2 * i) / N + (i % 2 ? 0.2 : 0);
      const sp = 4 + (i % 7);
      return {
        x: cx,
        y: cy,
        vx: Math.cos(ang) * sp * (0.6 + (i % 5) / 5),
        vy: Math.sin(ang) * sp - 3, // hơi bắn lên
        size: 4 + (i % 4) * 2,
        rot: i,
        vr: (i % 2 ? 1 : -1) * (0.1 + (i % 5) / 20),
        color: colors[i % colors.length],
        life: 1,
      };
    });

    let raf = 0;
    let frame = 0;
    const render = () => {
      frame++;
      ctx.clearRect(0, 0, W, H);
      let alive = 0;
      for (const p of parts) {
        p.vy += 0.16; // trọng lực
        p.vx *= 0.99;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.life -= 0.012;
        if (p.life <= 0) continue;
        alive++;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 1.6);
        ctx.restore();
      }
      if (alive > 0 && frame < 200) {
        raf = requestAnimationFrame(render);
      } else {
        onDone?.();
      }
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [fire, colors, onDone]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 z-20 h-full w-full"
      aria-hidden
    />
  );
};

export default RewardBurst;
